import { useCallback, useEffect, useState } from 'react'
import type { CrucialReactProps } from './crucialReactMount'
import QuestionCard from './QuestionCard'
import type { QuestionData } from './questionTypes'
import type { ThemeMode } from './themeHandler'
import { shuffleArray } from './questionUtils'
import './StudyModeQuestion.css'

export type StudyModeConfig = CrucialReactProps & {
  question: QuestionData
  canVote: boolean
  subjectId?: number
  xpPerCorrectQuestion?: number
  streak?: {
    value?: string | null
    title?: string | null
    launchConfetti?: boolean
    isRecord?: boolean
  }
}

type GradeResult = {
  correct: boolean
  answered: boolean
  selectedIds: number[]
}

type MatchingResponsePair = {
  prompt: string
  selectedAnswer: string
}

const getSidebar = () => document.getElementById('studyModeSidebar')
const buildMatchingResponses = (question: QuestionData, selections: string[]): MatchingResponsePair[] =>
  (question.matching?.items ?? []).map((item, index) => ({
    prompt: item.prompt,
    selectedAnswer: selections[index] ?? '',
  }))

const formatSessionQuestionCount = (count: number) => `${count} ${count === 1 ? 'question' : 'questions'} this session`

export default function StudyModeQuestion({
  question,
  canVote,
  subjectId,
  xpPerCorrectQuestion = 0,
  streak,
  theme,
}: StudyModeConfig & { theme: ThemeMode }) {
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<number[]>([])
  const [textAnswer, setTextAnswer] = useState('')
  const [matchingSelections, setMatchingSelections] = useState<string[]>([])
  const [isGraded, setIsGraded] = useState(false)
  const [gradeResult, setGradeResult] = useState<GradeResult | null>(null)
  const [shouldPromptUnanswered, setShouldPromptUnanswered] = useState(true)

  useEffect(() => {
    if (question.kind !== 'Ordered') return
    const ids = question.answers.map((answer) => answer.id)
    setSelectedAnswerIds(shuffleArray(ids))
  }, [question.id, question.kind])

  useEffect(() => {
    if (question.kind !== 'Matching') {
      setMatchingSelections([])
      return
    }
    const itemCount = question.matching?.items.length ?? 0
    setMatchingSelections(Array.from({ length: itemCount }, () => ''))
  }, [question.id, question.kind, question.matching?.items.length])

  const updateScoreCard = useCallback((forceTotalScore = false) => {
    const sidebar = getSidebar()
    if (!sidebar) return

    let totalCorrect = 0
    let totalIncorrect = 0

    sidebar.querySelectorAll<HTMLElement>('[data-objective-score]').forEach((element) => {
      const objId = element.dataset.objectiveScore
      if (!objId) return

      const correct = Number(window.localStorage.getItem(`sm-obj-${objId}-correct`) || 0)
      const incorrect = Number(window.localStorage.getItem(`sm-obj-${objId}-incorrect`) || 0)
      const total = correct + incorrect
      const score = total > 0 ? `${Math.round((correct / total) * 100)}%` : 'N/A'
      totalCorrect += correct
      totalIncorrect += incorrect
      element.textContent = score
    })

    const totalQuestions = totalCorrect + totalIncorrect
    const sessionCounter = sidebar.querySelector<HTMLElement>('[data-study-session-question-count]')
    if (sessionCounter) {
      sessionCounter.textContent = formatSessionQuestionCount(totalQuestions)
    }

    if (forceTotalScore || totalCorrect + totalIncorrect > 0) {
      const percent = totalQuestions > 0 ? totalCorrect / totalQuestions : NaN
      const totalScore = Number.isNaN(percent) ? 'N/A' : `${Math.round(percent * 100)}%`
      const scoreEl = sidebar.querySelector<HTMLElement>('#score')
      if (scoreEl) scoreEl.textContent = totalScore

      const scoreProgress = sidebar.querySelector<HTMLElement>('#scoreProgress')
      if (scoreProgress) {
        scoreProgress.style.width = totalScore
        const progressWrapper = scoreProgress.parentElement
        if (progressWrapper) progressWrapper.style.visibility = 'visible'
      }
    }
  }, [])

  const wipeScore = useCallback(() => {
    const confirmWipe = window.confirm('Are you sure you want to reset your score for this subject?')
    if (!confirmWipe) {
      return
    }

    const sidebar = getSidebar()
    if (!sidebar) return

    sidebar.querySelectorAll<HTMLElement>('[data-objective-score]').forEach((element) => {
      const objId = element.dataset.objectiveScore
      if (!objId) return

      window.localStorage.removeItem(`sm-obj-${objId}-correct`)
      window.localStorage.removeItem(`sm-obj-${objId}-incorrect`)
    })

    updateScoreCard(true)
  }, [updateScoreCard])

  useEffect(() => {
    window.trackStartTime?.()
    updateScoreCard()
    ;(window as Window & { wipeScore?: () => void }).wipeScore = wipeScore
  }, [updateScoreCard, wipeScore])

  const bumpTotalStudyModeCounter = () => {
    const key = 'sm-total-questions'
    const totalQuestions = Number(window.localStorage.getItem(key) || 0) + 1
    window.localStorage.setItem(key, totalQuestions.toString())
  }

  const gradeChoiceQuestion = (): GradeResult => {
    let allCorrect = true
    let madeSelection = false

    question.answers.forEach((answer) => {
      const isSelected = selectedAnswerIds.includes(answer.id)
      if (isSelected) {
        madeSelection = true
        allCorrect = allCorrect && answer.isCorrect
      } else if (answer.isCorrect) {
        allCorrect = false
      }
    })

    return {
      correct: allCorrect,
      answered: madeSelection,
      selectedIds: selectedAnswerIds,
    }
  }

  const gradeTextInputQuestion = (): GradeResult => {
    const userInput = textAnswer.trim()
    const correctAnswers = question.textInput?.correctAnswers ?? []
    const isCorrect = correctAnswers.some((answer) => answer.trim().toUpperCase() === userInput.toUpperCase())

    return {
      correct: isCorrect,
      answered: userInput.length > 0,
      selectedIds: question.textInput?.answerId ? [question.textInput.answerId] : [],
    }
  }

  const gradeOrderedQuestion = (): GradeResult => {
    const correctOrder = question.answers.map((answer) => answer.id)
    const rawSelected = selectedAnswerIds.length > 0 ? selectedAnswerIds : correctOrder
    const uniqueSelected = Array.from(new Set(rawSelected.filter((id) => correctOrder.includes(id))))
    const missing = correctOrder.filter((id) => !uniqueSelected.includes(id))
    const selectedIds = [...uniqueSelected, ...missing]
    const isCorrect =
      selectedIds.length === correctOrder.length && selectedIds.every((id, index) => id === correctOrder[index])

    return {
      correct: isCorrect,
      answered: selectedIds.length > 0,
      selectedIds,
    }
  }

  const gradeMatchingQuestion = (): GradeResult => {
    const items = question.matching?.items ?? []
    const normalizedSelections = matchingSelections.map((value) => value.trim().toLowerCase())
    const answered = normalizedSelections.some((value) => value.length > 0)
    const correct =
      items.length > 0 &&
      items.every((item, index) => {
        const selected = normalizedSelections[index] ?? ''
        return selected.length > 0 && selected === item.answer.trim().toLowerCase()
      })

    return {
      correct,
      answered,
      selectedIds: [],
    }
  }

  const handleGrade = () => {
    if (isGraded) return

    const hasAnswer =
      question.kind === 'TextInput'
        ? textAnswer.trim().length > 0
        : question.kind === 'Matching'
          ? matchingSelections.some((value) => value.trim().length > 0)
        : question.kind === 'Ordered'
          ? selectedAnswerIds.length > 0
          : selectedAnswerIds.length > 0

    if (shouldPromptUnanswered && !hasAnswer) {
      const confirmSubmit = window.confirm("Are you sure?  You haven't selected any answers yet!")
      if (!confirmSubmit) {
        setShouldPromptUnanswered(false)
        return
      }
    }

    const result =
      question.kind === 'TextInput'
        ? gradeTextInputQuestion()
        : question.kind === 'Matching'
          ? gradeMatchingQuestion()
        : question.kind === 'Ordered'
          ? gradeOrderedQuestion()
          : gradeChoiceQuestion()
    setGradeResult(result)
    setIsGraded(true)

    if (result.correct) {
      const earnedStreak = Boolean(streak?.value || streak?.launchConfetti || streak?.isRecord)
      if (earnedStreak) {
        window.Sounds?.levelUp?.()
      } else {
        window.Sounds?.correct?.()
      }
      if (earnedStreak) {
        window.playStudyModeHotStreakEffect?.()
      }
      const xpMessage =
        xpPerCorrectQuestion > 0
          ? `You got this one correct! +${xpPerCorrectQuestion} experience.`
          : 'You got this one correct!'
      window.enqueueToast?.({
        theme: 'success',
        icon: 'fa fa-check-circle',
        title: 'Nice Job!',
        message: xpMessage,
        duration: 2500,
        showOnMobile: false,
      })
    } else {
      window.enqueueToast?.({
        theme: 'danger',
        icon: 'fa-solid fa-xmark',
        title: 'Uh oh!',
        message: "You didn't get this one correct.",
        duration: 2500,
        showOnMobile: false,
      })
    }

    if (result.correct) {
      const sidebar = getSidebar()
      if (sidebar) {
        sidebar.querySelectorAll<HTMLElement>('.show-on-grade-correct, .show-on-grade-correct-global').forEach((el) => {
          el.style.display = ''
        })
      }
    }

    if (result.correct && question.objectiveId) {
      const key = `sm-obj-${question.objectiveId}-correct`
      const currentVal = Number(window.localStorage.getItem(key) || 0) + 1
      window.localStorage.setItem(key, currentVal.toString())
    } else if (question.objectiveId) {
      const key = `sm-obj-${question.objectiveId}-incorrect`
      const currentVal = Number(window.localStorage.getItem(key) || 0) + 1
      window.localStorage.setItem(key, currentVal.toString())
    }

    bumpTotalStudyModeCounter()
    updateScoreCard()

    if (result.correct && streak?.launchConfetti) {
      window.launchConfetti?.()
    }

    if (!question.skipSaveResponse) {
      const matchingResponses =
        question.kind === 'Matching' ? buildMatchingResponses(question, matchingSelections) : null
      window.sendQuestionResponse?.(
        question.id,
        result.selectedIds,
        result.correct,
        false,
        'study',
        null,
        question.setId ?? null,
        matchingResponses,
      )
    }
  }

  return (
    <div className="sm-wrapper cer-shell" data-theme={theme}>
      <QuestionCard
        question={question}
        mode="study"
        selectedAnswerIds={selectedAnswerIds}
        textAnswer={textAnswer}
        matchingSelections={matchingSelections}
        onSelectAnswer={(answerId) =>
          setSelectedAnswerIds((prev) => {
            if (question.kind === 'MultipleChoice') {
              return prev.includes(answerId) ? prev.filter((id) => id !== answerId) : [...prev, answerId]
            }
            return [answerId]
          })
        }
        onOrderChange={(nextIds) => setSelectedAnswerIds(nextIds)}
        onTextAnswerChange={setTextAnswer}
        onMatchingChange={(index, value) =>
          setMatchingSelections((prev) => {
            const next = [...prev]
            next[index] = value
            return next
          })
        }
        isGraded={isGraded}
        gradeResult={gradeResult}
        showAnswerButton
        showAnswerLabel="Show Answer"
        onShowAnswer={handleGrade}
        canVote={canVote}
        showAiChatLink
        aiChatButton={
          subjectId
            ? {
                chatId: `studymode_${question.id}`,
                subjectId,
                questionId: question.id,
              }
            : null
        }
      />
    </div>
  )
}
